<template>
  <ol class="stays">
    <li
      v-for="(stay, index) in stays"
      :key="`${stay.id}-${index}`"
      class="stay"
      tabindex="0"
      role="button"
      @click="$emit('select', stay)"
      @keydown.enter="$emit('select', stay)"
    >
      <!-- The lane: the stop's number, and the dashed curve down to the next
           one. LTR inside so the SVG's x and the dot's left agree. -->
      <span class="stay-track" aria-hidden="true">
        <svg
          v-if="index < stays.length - 1"
          class="stay-link"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <path
            :d="linkPath(index)"
            vector-effect="non-scaling-stroke"
          />
        </svg>
        <span class="stay-dot" :style="{ left: `${dotCenter(index) - DOT_SIZE / 2}px` }">{{ index + 1 }}</span>
      </span>
      <span class="stay-text">
        <span class="stay-name">{{ stay.locationName }}</span>
        <span class="stay-nights">
          {{ stay.numberOfDays > 1 ? t('nights', { count: stay.numberOfDays }) : t('oneNight') }}
        </span>
      </span>
    </li>
  </ol>
</template>

<script>
import { translationMixin } from "@/mixins/translationMixin";

// Lane geometry, in px. The dots alternate between two centres, so the curve
// between consecutive stops snakes from one side of the lane to the other.
const TRACK_WIDTH = 104;
const DOT_SIZE = 44;
const DOT_CENTERS = [DOT_SIZE / 2 + 4, TRACK_WIDTH - DOT_SIZE / 2 - 4];

/**
 * The classic summary's stays: numbered stops joined by a dashed curve, each
 * with its place and its nights. Drawn, not pictures, so it scales to any
 * width and the names never sit on top of the numbers.
 *
 * Emits 'select' with the stay when one is clicked.
 */
export default {
  name: 'ClassicStaysTimeline',
  mixins: [translationMixin],
  props: {
    // allRide.highLevelRideLocations: { id, locationName, numberOfDays }
    stays: { type: Array, default: () => [] },
  },
  data() {
    return { DOT_SIZE };
  },
  methods: {
    dotCenter(index) {
      return DOT_CENTERS[index % 2];
    },
    // From this dot's centre (top of the SVG) to the next dot's centre (its
    // bottom), as an S-curve. In viewBox units: x is 0..100 across the lane,
    // y 0..100 down the row, stretched to the row's real height.
    linkPath(index) {
      const x1 = (this.dotCenter(index) / TRACK_WIDTH) * 100;
      const x2 = (this.dotCenter(index + 1) / TRACK_WIDTH) * 100;
      return `M ${x1} 0 C ${x1} 55, ${x2} 45, ${x2} 100`;
    },
  },
};
</script>

<style scoped>
/* Sizes in px: the app's root font is 10px, so rem here would be tiny. */
.stays {
  --stay-row: 92px;
  --stay-dot: 44px;
  --stay-dot-top: 4px;

  list-style: none;
  margin: 0 auto;
  padding: 0;
  /* The summary's wrappers shrink to their content, so a bare 100% collapses
     and names break mid-word; this keeps a readable column on any screen. */
  width: min(400px, 88vw);
}

.stay {
  display: flex;
  align-items: stretch;
  gap: 14px;
  min-height: var(--stay-row);
  padding: 0 8px;
  border-radius: 14px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

/* Hover only where there is a mouse: on touch it sticks to the last row tapped. */
@media (hover: hover) {
  .stay:hover {
    background: rgba(0, 0, 0, 0.035);
  }
  .stay:hover .stay-dot {
    transform: scale(1.08);
  }
}

.stay:focus-visible {
  background: rgba(0, 0, 0, 0.035);
  outline: none;
}

.stay-track {
  position: relative;
  flex: 0 0 104px;
  direction: ltr;
}

/* From this dot's centre down to the next row's dot centre. */
.stay-link {
  position: absolute;
  left: 0;
  top: calc(var(--stay-dot-top) + var(--stay-dot) / 2);
  width: 100%;
  height: 100%;
  overflow: visible;
}

.stay-link path {
  fill: none;
  /* --primary-color, not --primary-background: that one can be a gradient,
     which is no stroke at all. */
  stroke: var(--primary-color, #07A0C3);
  stroke-width: 2.5;
  stroke-dasharray: 6 6;
  stroke-linecap: round;
  opacity: 0.6;
  /* The dashes travel from this stop towards the next. */
  animation: stay-route 1.2s linear infinite;
}

@keyframes stay-route {
  to { stroke-dashoffset: -12; }
}

@media (prefers-reduced-motion: reduce) {
  .stay-link path {
    animation: none;
  }
}

.stay-dot {
  position: absolute;
  top: var(--stay-dot-top);
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--stay-dot);
  height: var(--stay-dot);
  border-radius: 50%;
  background: var(--primary-background, #07A0C3);
  color: var(--primary-text-color, #fff);
  font-size: 18px;
  font-weight: 700;
  box-shadow: 0 0 0 5px #fff, 0 6px 14px -6px rgba(0, 0, 0, 0.45);
  transition: transform 0.2s ease;
}

.stay:focus-visible .stay-dot {
  transform: scale(1.08);
}

.stay-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  padding: 8px 0 18px;
  text-align: start;
}

.stay-name {
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
  color: #1d1d1f;
  overflow-wrap: break-word;
}

.stay-nights {
  font-size: 15px;
  font-weight: 600;
  color: var(--primary-color, #07A0C3);
}

@media (max-width: 480px) {
  .stay-name {
    font-size: 16px;
  }
}
</style>
